import { Block } from "../models/Block";
import { TextNode } from "../models/TextNode";
import { Modifier } from "../types";

function textInlineStyle(style, raw = false) {
	let inline = "";

	for (const key in style) {
		switch (key) {
			case Modifier.background:
				inline += `background: ${style[key]};`;
				break;
			case Modifier.color:
				inline += `color: ${style[key]};`;
				break;
			case Modifier.fontFamily:
				inline += `font-family: ${style[key]};`;
				break;
			case Modifier.fontSize:
				inline += `font-size: ${style[key]};`;
				break;
		}
	}
	if (raw) {
		return inline;
	}
	if (inline) {
		return ` style="${inline}"`;
	}
	return "";
}

function tagWithContent(tags, inlineStyle, content) {
	if (tags.length === 0 && content.trim() === "") {
		tags.push("span");
	}
	const openTags = tags.reduce(
		(res: string, tag: string, i: number) => res + (i === 0 ? `<${tag}${inlineStyle}>` : `<${tag}>`),
		""
	);
	const closeTags = tags.reduceRight((res: string, tag: string) => res + `</${tag}>`, "");

	return openTags + content + closeTags;
}

function repeatStr(str, count) {
	let res = "";
	while (count--) {
		res += str;
	}
	return res;
}

function processText(text: string) {
	return text
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/\n/g, "<br>")
		.replace(/[ ]{2,}/g, match => " " + repeatStr("&nbsp;", match.length - 1));
}

function serializeTextNode(textNode: TextNode, raw = false) {
	const { style, text } = textNode;

	const tags = [];

	if (style[Modifier.underline]) {
		tags.push("u");
	}
	if (style[Modifier.strike]) {
		tags.push("s");
	}
	if (style[Modifier.bold]) {
		tags.push("strong");
	}
	if (style[Modifier.italic]) {
		tags.push("i");
	}

	if (raw) {
		return {
			tags,
			inline: textInlineStyle(style, true),
			text: processText(text),
		};
	}

	const inlineStyle = textInlineStyle(style);

	if (inlineStyle && tags.length === 0) {
		tags.push("span");
	}
	return tagWithContent(tags, inlineStyle, processText(text));
}

function serializeTextNodes(textNodes: TextNode[]) {
	let res = "";
	let linkContent = "";
	let linkText = "";
	let prevLink;

	for (const textNode of textNodes) {
		if (textNode.style.link) {
			if (prevLink === textNode.style.link) {
				linkContent += serializeTextNode(textNode);
				linkText += textNode.text.replace(/"/g, "'");
			} else {
				linkContent = serializeTextNode(textNode);
				linkText = textNode.text.replace(/"/g, "'");
			}
		} else {
			if (prevLink) {
				res += tagWithContent(["a"], ` href="${prevLink}" title="${linkText}"`, linkContent);
			}
			res += serializeTextNode(textNode);
		}
		prevLink = textNode.style.link;
	}
	if (prevLink) {
		res += tagWithContent(["a"], ` href="${prevLink}" title="${linkText}"`, linkContent);
	}
	return res;
}

function serializeBlock(block: Block) {
	let inline = "";

	const { style, textNodes } = block;

	const tags = [];
	if (block.style[Modifier.blockquote]) {
		tags.push("blockquote");
	}
	tags.push(style[Modifier.style] || "p");

	const align = style[Modifier.align] ? `text-align: ${style[Modifier.align]};` : "";

	if (textNodes.length === 1 && !textNodes[0].style[Modifier.link]) {
		const res = serializeTextNode(textNodes[0], true);
		tags.push(...res.tags);
		const textInline = res.inline;

		if (textInline || align) {
			inline = ` style="${textInline}${align}"`;
		}

		return tagWithContent(tags, inline, res.text);
	}

	if (align) {
		inline = ` style="${align}"`;
	}

	return tagWithContent(tags, inline, serializeTextNodes(textNodes));
}

export function serializer(blocks: Block[]) {
	return blocks.reduce((result, block) => result + serializeBlock(block), "");
}
